import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
    fetchData,
    findMenuWithParent,
    formatDate,
    getEncryptedRoute,
    toasterrormsg,
    toastsuccessmsg,
    useMenuIdPermissions,
} from '../../../reusable'
import Swal from 'sweetalert2'
import { ToastContainer } from 'react-toastify'
import DataTable from 'react-data-table-component'
import { Tr } from '../../../_metronic/i18n/Tr'
import { KTIcon } from '../../../_metronic/helpers'
import Loader from '../components/Loader'
import { useMenuContext } from '../contexts/MenuContext'


const ReportedPosts: FC = () => {
    let t = Tr()
    const { menuId, menuIdEdit, menuIdDelete, menuIdView } = useMenuIdPermissions()

    const { fullMenu } = useMenuContext()
    const result = findMenuWithParent(fullMenu, "../postmanagement/");
    const postManagementId = result?.menu?.menuId

    const [reportedPostList, setReportedPostList] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [totalRecords, setTotalRecords] = useState(0)
    const [limit, setLimit] = useState(10)
    const [offset, setOffset] = useState(0)
    const [sortField, setSortField] = useState('')
    const [sortOrder, setSortOrder] = useState('')
    const [isDataLoaded, setIsDataLoaded] = useState(false)
    const [focus, setFocus] = useState('')
    const [selectedReportedType, setSelectedReportedType] = useState("ADMIN")
    const adminData: any = sessionStorage.getItem("admin");
    let adminId = "";
    if (adminData) {
        adminId = JSON.parse(adminData).adminId || "";
    }


    const reportedPostListApiCall = async () => {
        try {
            if (!isDataLoaded) {
                setIsLoading(true)
            }
            const param = {
                limit: limit,
                offset: offset,
                term: searchTerm.trim(),
                sortBy: sortField,
                sort: sortOrder,
                type: selectedReportedType
            }
            const response = await fetchData('admin/explore/post/report/list', param)

            if (response.status == '200') {
                const data = response.data
                setTotalRecords(data.totalRecord || 0)
                setReportedPostList(data.list || [])
                setIsDataLoaded(true)
            } else {
                toasterrormsg(response.message)
                setReportedPostList([])
                setTotalRecords(0)
            }
        } catch (error) {
            console.error('Error fetching reported post list:', error)
            toasterrormsg('Failed to fetch reported post')
            setReportedPostList([])
            setTotalRecords(0)
        } finally {
            setIsLoading(false)
        }
    }

    const setPostReviewed = async (reportId: number) => {
        let swalOptions: any = {
            icon: 'warning',
            title: t('Are you sure you want to review this report?'),
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }


        const result = await Swal.fire(swalOptions)
        if (!result.isConfirmed) return

        const param: any = {
            reportId,
            adminId,
        }


        try {
            setIsLoading(true)
            const response = await fetchData('admin/explore/report/review', param)

            if (String(response?.status) === '200') {
                reportedPostListApiCall()
                toastsuccessmsg(response.message)
            } else {
                toasterrormsg(response?.message || 'Action failed')
            }
        } catch (error) {
            toasterrormsg('Something went wrong. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleTabChange = (status: string) => {
        setSelectedReportedType(status)
        setOffset(0)
        setSearchTerm('')
        setSortField('')
        setSortOrder('')
    }

    const confirmdelete = (postId: any) => {
        Swal.fire({
            title: 'Are you sure?',
            html: `
            <p>Are you sure you want to delete this post?</p>
            <small style="color:#6c757d">
                Once deleted, this post will be permanently removed and will not be visible anywhere.
            </small>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setIsLoading(true)
                    const param = {
                        postId: postId,
                        adminId,
                    }
                    const response = await fetchData('admin/explore/post/delete', param, "DELETE")

                    if (response.status == '200') {
                        toastsuccessmsg(response.message || 'Deleted Successfully')
                        reportedPostListApiCall()
                    } else {
                        toasterrormsg(response.message)
                    }
                } catch (error) {
                    console.error('Error deleting post:', error)
                    toasterrormsg('Failed to delete post')
                } finally {
                    setIsLoading(false)
                }
            }
        })
    }

    const reportedPostSearch = (text: string, isfoc: any) => {
        setSearchTerm(text)
        setFocus(isfoc)

        setOffset(0)
    }

    const handleSort = (column: any, sortDirection: string) => {
        try {
            const fieldName = column.selector ? String(column.selector).split('.').pop() : ''
            setSortField(fieldName || '')
            setSortOrder(sortDirection === 'asc' ? 'asc' : 'desc')
        } catch (error) {
            console.error('Error in sorting:', error)
        }
    }


    const CustomHeader = (props: any) => {
        const inputRef: any = useRef(null)

        // Define the onchange event handler
        const handleChange = async (e: any) => {
            // Focus on the input element when the onchange event is triggered
            await props.customSearch(e, 1)
        }
        useEffect(() => {
            // Check if the ref exists and if the component should be focused
            if (inputRef.current && props.isfoc === 1) {
                inputRef.current.focus()
            }
        }, [props.isfoc])

        return (
            <div className=' d-flex justify-content-between w-100 my-5'>
                <div className='d-flex align-items-center position-relative my-1 '>
                    <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
                    <input
                        type='text'
                        data-kt-post-table-filter='search'
                        className='form-control form-control-solid w-250px ps-14'
                        placeholder={t('Search...')}
                        value={props.search}
                        onChange={(e: any) => handleChange(e.target.value)}
                        ref={inputRef}
                    />
                </div>

            </div>
        )
    }

    const column = useMemo(
        () => [
            {
                name: t('Reported By'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.reporterName || '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.reporterName,
                width: "250px"
            },
            {
                name: t('Destination Name'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.destinationName || '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.destinationName,
                width: "250px"
            },
            {
                name: t('Violation'),
                cell: (row: any) => {
                    const violationMap: Record<string, string> = {
                        false_information: t('False information'),
                        spam: t('Spam'),
                        other: t('Other'),
                    }
                    const key = (row.violation || '').toLowerCase().trim()
                    return (
                        <div className='my-3'>
                            <div className='fw-bold fs-7'>{violationMap[key] || row.violation || '-'}</div>
                        </div>
                    )
                },
                sortable: true,
                selector: (row: any) => row.violation,
                width: "250px"
            },
            {
                name: t('Report Reason'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{row.reason || '-'}</div>
                    </div>
                ),
                sortable: false,
                selector: (row: any) => row.reason,
                width: "350px"

            },

            {
                name: t('Status'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{
                            row.isReview ? (
                                <>
                                    <span
                                        className='btn btn-success mx-2 p-1 btn-sm fw-bold pointer-events-none status_width'
                                        style={{ marginTop: 0 }}
                                    >
                                        {t('Reviewed')}
                                    </span>
                                </>
                            ) : <span
                                className='btn btn-warning mx-2 p-1 btn-sm fw-bold pointer-events-none status_width'
                                style={{ marginTop: 0 }}
                            >
                                {t('Not Reviewed')}
                            </span>
                        }</div>
                    </div>
                ),
                sortable: false,
                selector: (row: any) => row.isReview,
                width: "150px"

            },
            {
                name: t('Reported At'),
                cell: (row: any) => (
                    <div className='my-3'>
                        <div className='fw-bold fs-5'>{formatDate(row.created, 'DD-MM-YYYY') || '-'}</div>
                    </div>
                ),
                sortable: true,
                selector: (row: any) => row.created,
                width: "130px"

            },
            {
                name: t('Action'),
                cell: (row: any) => (
                    <div className='d-flex gap-2'>
                        {menuIdEdit && !row.isReview && (
                            <>
                                <button
                                    onClick={() => setPostReviewed(row.reportId)}
                                    className='btn btn-sm btn-icon btn-light-success'
                                    title='Review Report'
                                >
                                    <KTIcon iconName='check-circle' className='fs-2' />
                                </button>

                            </>
                        )}
                        {menuIdView && (
                            <Link
                                to={getEncryptedRoute('../explore/user/', {
                                    menuId: postManagementId,
                                    userId: row.userId,
                                    postId: row.postId,
                                })}
                                state={{ postId: row.postId || row.id }}
                                className='btn btn-sm btn-icon btn-light-primary'
                                title='View'
                            >
                                <KTIcon iconName='eye' className='fs-2' />
                            </Link>
                        )}


                        {menuIdDelete && (
                            <button
                                onClick={() => confirmdelete(row.postId || row.id)}
                                className='btn btn-sm btn-icon btn-light-danger'
                                title='Delete'
                            >
                                <KTIcon iconName='trash' className='fs-2' />
                            </button>
                        )}


                    </div>
                ),
                width: '180px',
            },
        ],
        [t, menuIdView, menuIdEdit, menuIdDelete, menuId, selectedReportedType]
    )

    useEffect(() => {
        reportedPostListApiCall()
    }, [limit, offset, sortField, sortOrder, searchTerm, selectedReportedType])

    useEffect(() => {
        document.title = 'Insayr | Reported Posts'
    }, [])

    const handlePageChange = (newPage: number) => {
        try {
            const totalPages = Math.ceil(totalRecords / limit)

            if (newPage === 1) {
                setOffset(0)
            } else if (newPage === totalPages) {
                setOffset((totalPages - 1) * limit)
            } else {
                setOffset((newPage - 1) * limit)
            }
        } catch (error) {
            console.error('Error in page change:', error)
        }
    }

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        try {
            setLimit(newRowsPerPage)
            setOffset(0)
        } catch (error) {
            console.error('Error in rows per page change:', error)
        }
    }

    return (
        <>
            <div className='card mb-5 mb-xl-10'>
                <div className='card-body'>
                    <div className='d-flex overflow-auto'>
                        <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder my-2 flex-nowrap'>


                            <li className='nav-item'>
                                <button
                                    className={`nav-link text-active-primary me-6 p-3 text-nowrap ${selectedReportedType == 'ADMIN' ? 'bg-success text-white rounded' : 'nav-link text-active-primary me-6 p-3 text-nowrap bg-primary text-white rounded'
                                        }`}
                                    onClick={() => handleTabChange('ADMIN')}
                                >
                                    {t('Admin')}
                                </button>
                            </li>
                            <li className='nav-item'>
                                <button
                                    className={`nav-link text-active-primary me-6 p-3 text-nowrap ${selectedReportedType == 'USER' ? 'bg-success text-white rounded' : 'nav-link text-active-primary me-6 p-3 text-nowrap bg-primary text-white rounded'
                                        }`}
                                    onClick={() => handleTabChange('USER')}
                                >
                                    {t('User')}
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <ToastContainer />
            {isLoading && <Loader />}

            <DataTable
                columns={column}
                data={reportedPostList}
                pagination
                paginationServer={true}
                onChangePage={handlePageChange}
                paginationTotalRows={totalRecords}
                paginationPerPage={limit}
                onChangeRowsPerPage={handleRowsPerPageChange}
                paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
                sortServer={true}
                onSort={handleSort}
                subHeader={true}
                subHeaderComponent={<CustomHeader search={searchTerm} isfoc={focus} customSearch={reportedPostSearch} />}
                paginationComponentOptions={{
                    rowsPerPageText: t('Rows Per Page'),
                }}
            />
        </>
    )
}

export { ReportedPosts }